import { useEffect, useState } from 'react';
import Layout from '../components/layout/Layout.tsx';
import {
  fetchTransbordos,
  createTransbordo,
  updateTransbordo,
  deleteTransbordo,
} from '../services/api.ts';
import { Transbordo } from '../types';
import { toast } from 'react-toastify';
import { AlertTriangle, Pencil, PhoneForwarded, Plus, Trash2, X } from 'lucide-react';

const splitPhones = (value: string) => (
  value
    .split(/[,;\n]+/)
    .map(phone => phone.trim())
    .filter(Boolean)
);

const hasValidPhone = (phone: string) => {
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 10;
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { error?: string; message?: string } } }).response;
    return response?.data?.error || response?.data?.message || fallback;
  }
  return error instanceof Error ? error.message : fallback;
};

const FIELD_TOOLTIPS = {
  motivo: 'Termo, frase ou intenção que faz a IA acionar o atendimento humano.',
  telefones: 'Números que recebem o aviso do transbordo. Use DDD e separe por vírgula, ponto e vírgula ou quebra de linha.',
  mensagemCliente: 'Mensagem opcional enviada ao cliente quando o transbordo é ativado.',
  mensagemTransbordo: 'Mensagem opcional enviada para a equipe junto com o aviso de transbordo.',
  pausarBot: 'Quando ativo, a IA para de responder esse cliente depois do transbordo.',
};

const InfoTip = ({ text }: { text: string }) => (
  <span className="group relative inline-flex items-center">
    <span
      className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-300 bg-white text-[10px] font-bold leading-none text-slate-500 outline-none transition group-focus-within:border-teal-500 group-focus-within:text-teal-700"
      aria-label={text}
      role="img"
      tabIndex={0}
    >
      i
    </span>
    <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden w-max max-w-[240px] -translate-x-1/2 whitespace-normal rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium leading-4 text-slate-600 shadow-lg group-hover:block group-focus-within:block">
      {text}
    </span>
  </span>
);

const FieldLabel = ({
  children,
  tooltip,
  required = false,
  htmlFor,
  small = false,
}: {
  children: string;
  tooltip: string;
  required?: boolean;
  htmlFor?: string;
  small?: boolean;
}) => (
  <label
    htmlFor={htmlFor}
    className={`mb-1 flex items-center gap-2 font-medium text-gray-700 ${small ? 'text-xs' : 'text-sm'}`}
  >
    <span>
      {children}
      {required && <span className="text-red-500"> *</span>}
    </span>
    <InfoTip text={tooltip} />
  </label>
);

const DeleteTransbordoModal = ({
  target,
  saving,
  onCancel,
  onConfirm,
}: {
  target: Transbordo | null;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  if (!target) return null;

  const phones = target.atendabot_transbordo_numeros || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600">
              <AlertTriangle size={20} />
            </span>
            <div>
              <h3 className="text-base font-bold text-slate-900">Excluir transbordo?</h3>
              <p className="mt-1 text-sm leading-5 text-slate-600">
                Esta regra deixará de acionar a transferência automática de atendimento.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
            aria-label="Fechar modal"
          >
            <X size={18} />
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          <div className="font-semibold text-slate-900">{target.motivo}</div>
          <div className="mt-2 text-xs text-slate-500">
            {phones.length > 0
              ? `Telefones: ${phones.map((numero) => numero.telefone).join(', ')}`
              : 'Nenhum telefone cadastrado'}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Pausar IA: {target.pausar_bot ? 'Sim' : 'Não'}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 text-sm font-bold text-white shadow-lg shadow-rose-600/20 transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Excluindo...' : 'Excluir transbordo'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TransbordoPage = () => {
  const [items, setItems] = useState([] as Transbordo[]);
  const [loading, setLoading] = useState(true);
  const [newMotivo, setNewMotivo] = useState('');
  const [newMensagemCliente, setNewMensagemCliente] = useState('');
  const [newMensagemTransbordo, setNewMensagemTransbordo] = useState('');
  const [newPausarBot, setNewPausarBot] = useState(false);
  const [newPhones, setNewPhones] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editMotivo, setEditMotivo] = useState('');
  const [editMensagemCliente, setEditMensagemCliente] = useState('');
  const [editMensagemTransbordo, setEditMensagemTransbordo] = useState('');
  const [editPausarBot, setEditPausarBot] = useState(false);
  const [editPhones, setEditPhones] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Transbordo | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchTransbordos();
      setItems(data);
    } catch (e) {
      console.error(e);
      toast.error(getErrorMessage(e, 'Erro ao carregar transbordos.'));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async () => {
    const motivo = newMotivo.trim();
    const phones = splitPhones(newPhones);
    const invalidPhones = phones.filter(phone => !hasValidPhone(phone));

    if (!motivo) {
      toast.warning('Informe o motivo do transbordo.');
      return;
    }
    if (phones.length === 0) {
      toast.warning('Informe pelo menos um telefone para receber o transbordo.');
      return;
    }
    if (invalidPhones.length > 0) {
      toast.warning('Informe telefones com DDD.');
      return;
    }

    setSaving(true);
    try {
      const created = await createTransbordo({
        motivo,
        mensagem_cliente: newMensagemCliente || undefined,
        mensagem_transbordo: newMensagemTransbordo || undefined,
        pausar_bot: newPausarBot,
        phones
      });
      setItems([...items, created]);
      setNewMotivo('');
      setNewMensagemCliente('');
      setNewMensagemTransbordo('');
      setNewPausarBot(false);
      setNewPhones('');
      toast.success('Transbordo adicionado com sucesso.');
    } catch (e) {
      console.error(e);
      toast.error(getErrorMessage(e, 'Erro ao adicionar transbordo.'));
    }
    setSaving(false);
  };

  const handleUpdate = async (id: number) => {
    const motivo = editMotivo.trim();
    const phones = splitPhones(editPhones);
    const invalidPhones = phones.filter(phone => !hasValidPhone(phone));

    if (!motivo) {
      toast.warning('Informe o motivo do transbordo.');
      return;
    }
    if (phones.length === 0) {
      toast.warning('Informe pelo menos um telefone para receber o transbordo.');
      return;
    }
    if (invalidPhones.length > 0) {
      toast.warning('Informe telefones com DDD.');
      return;
    }

    setSaving(true);
    try {
      await updateTransbordo(id, {
        motivo,
        mensagem_cliente: editMensagemCliente || undefined,
        mensagem_transbordo: editMensagemTransbordo || undefined,
        pausar_bot: editPausarBot,
        phones
      });
      setItems(items.map(it => it.id === id ? {
        ...it,
        motivo,
        mensagem_cliente: editMensagemCliente,
        mensagem_transbordo: editMensagemTransbordo,
        pausar_bot: editPausarBot,
        atendabot_transbordo_numeros: phones.map((phone, index) => ({
          id: index + 1,
          created_at: new Date().toISOString(),
          transbordo_id: id,
          telefone: phone
        }))
      } : it));
      setEditingId(null);
      setEditMotivo('');
      setEditMensagemCliente('');
      setEditMensagemTransbordo('');
      setEditPausarBot(false);
      setEditPhones('');
      toast.success('Transbordo atualizado com sucesso.');
    } catch (e) {
      console.error(e);
      toast.error(getErrorMessage(e, 'Erro ao atualizar transbordo.'));
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setSaving(true);
    try {
      await deleteTransbordo(id);
      setItems(items.filter(it => it.id !== id));
      setDeleteTarget(null);
      toast.success('Transbordo removido com sucesso.');
    } catch (e) {
      console.error(e);
      toast.error(getErrorMessage(e, 'Erro ao remover transbordo.'));
    }
    setSaving(false);
  };

  return (
    <Layout>
      <DeleteTransbordoModal
        target={deleteTarget}
        saving={saving}
        onCancel={() => {
          if (!saving) setDeleteTarget(null);
        }}
        onConfirm={handleDelete}
      />
      {loading ? (
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
        </div>
      ) : (
        <div className="min-h-full bg-[#f6f7fb] px-3 py-6 text-slate-900 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[1400px] space-y-5">
          <div className="mb-1 text-center">
            <h2 className="flex items-center justify-center gap-3 text-2xl font-black text-slate-900">
              <PhoneForwarded size={28} className="text-[#2f7d72]" />
              Controle de transferência de atendimento
            </h2>
            <p className="mx-auto mt-3 max-w-3xl text-sm leading-6 text-slate-600">
              Configure mensagens de transbordo para transferir conversas automaticamente.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6 mb-8 space-y-6 md:space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FieldLabel tooltip={FIELD_TOOLTIPS.motivo} required>
                  Motivo
                </FieldLabel>
                <input
                  type="text"
                  placeholder="Ex: Cliente insatisfeito"
                  className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                  value={newMotivo}
                  onChange={e => setNewMotivo(e.target.value)}
                />
              </div>

              <div>
                <FieldLabel tooltip={FIELD_TOOLTIPS.telefones} required>
                  Telefones
                </FieldLabel>
                <input
                  type="text"
                  placeholder="Ex: 5511999999999, 5511888888888"
                  className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                  value={newPhones}
                  onChange={e => setNewPhones(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">Separe os números por vírgula</p>
              </div>
            </div>

            <div>
              <FieldLabel tooltip={FIELD_TOOLTIPS.mensagemCliente}>
                Mensagem para o Cliente
              </FieldLabel>
              <textarea
                placeholder="Mensagem enviada ao cliente quando o transbordo for ativado"
                className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                rows={3}
                value={newMensagemCliente}
                onChange={e => setNewMensagemCliente(e.target.value)}
              />
            </div>

            <div>
              <FieldLabel tooltip={FIELD_TOOLTIPS.mensagemTransbordo}>
                Mensagem de Transbordo
              </FieldLabel>
              <textarea
                placeholder="Mensagem enviada aos números cadastrados"
                className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                rows={3}
                value={newMensagemTransbordo}
                onChange={e => setNewMensagemTransbordo(e.target.value)}
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="newPausarBot"
                className="mr-2 w-auto"
                checked={newPausarBot}
                onChange={e => setNewPausarBot(e.target.checked)}
              />
              <FieldLabel htmlFor="newPausarBot" tooltip={FIELD_TOOLTIPS.pausarBot}>
                Pausar IA após transbordo
              </FieldLabel>
            </div>

            <button
              onClick={handleAdd}
              disabled={saving}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#2f7d72] px-4 text-sm font-semibold text-white transition hover:bg-[#27685f] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Plus size={16} />
              Adicionar Transbordo
            </button>
          </div>

          {items.length === 0 ? (
            <div className="text-center text-gray-400 py-8">Nenhum transbordo cadastrado.</div>
          ) : (
            <div className="bg-white rounded-lg shadow">
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm table-auto">
                <thead className="bg-gray-50 text-xs font-medium text-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left">Motivo</th>
                    <th className="px-4 py-2 text-left">Telefones</th>
                    <th className="px-4 py-2 text-left">Pausar IA</th>
                    <th className="px-4 py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      {editingId === item.id ? (
                        <>
                          <td className="px-4 py-2 align-top">
                            <div className="space-y-3">
                              <div>
                                <FieldLabel tooltip={FIELD_TOOLTIPS.motivo} required small>
                                  Motivo
                                </FieldLabel>
                                <input
                                  type="text"
                                  className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                                  value={editMotivo}
                                  onChange={e => setEditMotivo(e.target.value)}
                                />
                              </div>
                              <div>
                                <FieldLabel tooltip={FIELD_TOOLTIPS.mensagemCliente} small>
                                  Mensagem para o Cliente
                                </FieldLabel>
                                <textarea
                                  className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                                  rows={2}
                                  placeholder="Mensagem enviada ao cliente quando o transbordo for ativado"
                                  value={editMensagemCliente}
                                  onChange={e => setEditMensagemCliente(e.target.value)}
                                />
                              </div>
                              <div>
                                <FieldLabel tooltip={FIELD_TOOLTIPS.mensagemTransbordo} small>
                                  Mensagem de Transbordo
                                </FieldLabel>
                                <textarea
                                  className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                                  rows={2}
                                  placeholder="Mensagem enviada aos números cadastrados"
                                  value={editMensagemTransbordo}
                                  onChange={e => setEditMensagemTransbordo(e.target.value)}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2 align-top">
                            <FieldLabel tooltip={FIELD_TOOLTIPS.telefones} required small>
                              Telefones
                            </FieldLabel>
                            <input
                              type="text"
                              className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                              value={editPhones}
                              onChange={e => setEditPhones(e.target.value)}
                            />
                          </td>
                          <td className="px-4 py-2 align-top">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={editPausarBot}
                                onChange={e => setEditPausarBot(e.target.checked)}
                              />
                              <InfoTip text={FIELD_TOOLTIPS.pausarBot} />
                            </div>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-right">
                            <div className="inline-flex gap-2">
                              <button
                                onClick={() => handleUpdate(item.id)}
                                disabled={saving}
                                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-[#2f7d72] px-3 text-xs font-semibold text-white transition hover:bg-[#27685f] disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                Salvar
                              </button>
                              <button
                                onClick={() => {
                                  setEditingId(null);
                                  setEditMotivo('');
                                  setEditMensagemCliente('');
                                  setEditMensagemTransbordo('');
                                  setEditPausarBot(false);
                                  setEditPhones('');
                                }}
                                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                              >
                                Cancelar
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2">
                            <div className="font-medium">{item.motivo}</div>
                            {item.mensagem_cliente && (
                              <div className="text-xs text-gray-500 mt-1">
                                Cliente: {item.mensagem_cliente}
                              </div>
                            )}
                            {item.mensagem_transbordo && (
                              <div className="text-xs text-gray-500 mt-1">
                                Transbordo: {item.mensagem_transbordo}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            {item.atendabot_transbordo_numeros.map((numero, index) => (
                              <div key={numero.id} className="text-sm">
                                {numero.telefone}
                                {index < item.atendabot_transbordo_numeros.length - 1 && ', '}
                              </div>
                            ))}
                          </td>
                          <td className="px-4 py-2">
                            {item.pausar_bot ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                                Sim
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                Não
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-right">
                            <div className="inline-flex gap-2">
                              <button
                                onClick={() => {
                                  setEditingId(item.id);
                                  setEditMotivo(item.motivo);
                                  setEditMensagemCliente(item.mensagem_cliente || '');
                                  setEditMensagemTransbordo(item.mensagem_transbordo || '');
                                  setEditPausarBot(item.pausar_bot || false);
                                  setEditPhones(item.atendabot_transbordo_numeros.map(n => n.telefone).join(', '));
                                }}
                                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 hover:border-slate-300"
                              >
                                <Pencil size={14} />
                                Editar
                              </button>
                              <button
                                onClick={() => setDeleteTarget(item)}
                                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-3 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 hover:border-rose-300"
                              >
                                <Trash2 size={14} />
                                Excluir
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
              <div className="md:hidden divide-y">
                {items.map(item => (
                  <div key={`${item.id}-mobile`} className="px-4 py-5 space-y-4 text-sm">
                    {editingId === item.id ? (
                      <div className="space-y-3">
                        <div>
                          <FieldLabel tooltip={FIELD_TOOLTIPS.motivo} required small>
                            Motivo
                          </FieldLabel>
                          <input
                            type="text"
                            className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                            value={editMotivo}
                            onChange={e => setEditMotivo(e.target.value)}
                          />
                        </div>
                        <div>
                          <FieldLabel tooltip={FIELD_TOOLTIPS.mensagemCliente} small>
                            Mensagem para o Cliente
                          </FieldLabel>
                          <textarea
                            className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                            rows={2}
                            placeholder="Mensagem enviada ao cliente quando o transbordo for ativado"
                            value={editMensagemCliente}
                            onChange={e => setEditMensagemCliente(e.target.value)}
                          />
                        </div>
                        <div>
                          <FieldLabel tooltip={FIELD_TOOLTIPS.mensagemTransbordo} small>
                            Mensagem de Transbordo
                          </FieldLabel>
                          <textarea
                            className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                            rows={2}
                            placeholder="Mensagem enviada aos números cadastrados"
                            value={editMensagemTransbordo}
                            onChange={e => setEditMensagemTransbordo(e.target.value)}
                          />
                        </div>
                        <div>
                          <FieldLabel tooltip={FIELD_TOOLTIPS.telefones} required small>
                            Telefones
                          </FieldLabel>
                          <input
                            type="text"
                            className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                            value={editPhones}
                            onChange={e => setEditPhones(e.target.value)}
                          />
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`editPausarBotMobile-${item.id}`}
                            className="mr-2 w-auto"
                            checked={editPausarBot}
                            onChange={e => setEditPausarBot(e.target.checked)}
                          />
                          <FieldLabel
                            htmlFor={`editPausarBotMobile-${item.id}`}
                            tooltip={FIELD_TOOLTIPS.pausarBot}
                            small
                          >
                            Pausar IA após transbordo
                          </FieldLabel>
                        </div>
                        <div className="flex flex-wrap gap-2 justify-end">
                          <button
                            onClick={() => handleUpdate(item.id)}
                            disabled={saving}
                            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-[#2f7d72] px-3 text-xs font-semibold text-white transition hover:bg-[#27685f] disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            Salvar
                          </button>
                          <button
                            onClick={() => {
                              setEditingId(null);
                              setEditMotivo('');
                              setEditMensagemCliente('');
                              setEditMensagemTransbordo('');
                              setEditPausarBot(false);
                              setEditPhones('');
                            }}
                            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="text-base font-semibold">{item.motivo}</div>
                          {item.mensagem_cliente && (
                            <div className="text-xs text-gray-500 mt-1">
                              Cliente: {item.mensagem_cliente}
                            </div>
                          )}
                          {item.mensagem_transbordo && (
                            <div className="text-xs text-gray-500 mt-1">
                              Transbordo: {item.mensagem_transbordo}
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-500 mb-1">Telefones</div>
                          <div className="text-sm text-gray-600">
                            {item.atendabot_transbordo_numeros.map((numero, index) => (
                              <span key={numero.id}>
                                {numero.telefone}
                                {index < item.atendabot_transbordo_numeros.length - 1 && ', '}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-500 mb-1">Pausar IA</div>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                              item.pausar_bot ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {item.pausar_bot ? 'Sim' : 'Não'}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2 justify-end">
                          <button
                            onClick={() => {
                              setEditingId(item.id);
                              setEditMotivo(item.motivo);
                              setEditMensagemCliente(item.mensagem_cliente || '');
                              setEditMensagemTransbordo(item.mensagem_transbordo || '');
                              setEditPausarBot(item.pausar_bot || false);
                              setEditPhones(item.atendabot_transbordo_numeros.map(n => n.telefone).join(', '));
                            }}
                            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 hover:border-slate-300"
                          >
                            <Pencil size={14} />
                            Editar
                          </button>
                          <button
                            onClick={() => setDeleteTarget(item)}
                            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-3 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 hover:border-rose-300"
                          >
                            <Trash2 size={14} />
                            Excluir
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default TransbordoPage;
